import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Mail, Save, Eye, Send, RotateCcw,
  ChevronDown, ChevronUp, AlertTriangle, Check,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem("session_token")}` };
}

function formatDelay(hours) {
  if (hours === 0) return "Sends immediately on enrolment";
  const days = hours / 24;
  if (days < 1) return `Sends after ${hours}h`;
  return `Sends after ${Math.round(days * 10) / 10}d`;
}

export default function EmailDripTemplates() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [expanded, setExpanded] = useState(null);
  // Local drafts keyed by template_key so unsaved edits aren't lost when collapsing
  const [drafts, setDrafts] = useState({});
  const [previewHtml, setPreviewHtml] = useState({});  // by key
  const [busy, setBusy] = useState({ key: null, action: null });

  // Defined inline inside useEffect to satisfy a strict custom lint
  // plugin that misflags `setX` calls when their defining function lives
  // outside the effect. The same fetch is re-invoked manually after each
  // save/reset via `reloadSteps`.
  const reloadSteps = async () => {
    const res = await axios.get(`${API}/email-drip/templates`, { headers: auth() });
    setSteps(res.data || []);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/email-drip/templates`, { headers: auth() });
        if (active) setSteps(res.data || []);
      } catch (err) {
        if (active) toast.error("Failed to load templates");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const draftFor = (s) => drafts[s.template_key] || { subject: s.subject, body_html: s.body_html };

  const setDraft = (key, patch) => {
    setDrafts((prev) => {
      const fallback = (() => {
        const s = steps.find((x) => x.template_key === key);
        return s ? { subject: s.subject, body_html: s.body_html } : { subject: "", body_html: "" };
      })();
      const current = prev[key] || fallback;
      return { ...prev, [key]: { ...current, ...patch } };
    });
  };

  const renderPreview = async (s) => {
    setBusy({ key: s.template_key, action: "preview" });
    try {
      const d = draftFor(s);
      const res = await axios.post(
        `${API}/email-drip/templates/${s.template_key}/preview`,
        { subject: d.subject, body_html: d.body_html },
        { headers: auth() }
      );
      setPreviewHtml((p) => ({ ...p, [s.template_key]: res.data }));
    } catch (err) {
      toast.error("Preview render failed");
    } finally {
      setBusy({ key: null, action: null });
    }
  };

  const saveTemplate = async (s) => {
    const d = draftFor(s);
    if (!d.subject?.trim() || !d.body_html?.trim()) {
      toast.error("Subject and body are both required");
      return;
    }
    setBusy({ key: s.template_key, action: "save" });
    try {
      await axios.put(
        `${API}/email-drip/templates/${s.template_key}`,
        { subject: d.subject, body_html: d.body_html },
        { headers: auth() }
      );
      toast.success("Saved");
      setDrafts((p) => { const c = { ...p }; delete c[s.template_key]; return c; });
      await reloadSteps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy({ key: null, action: null });
    }
  };

  const resetTemplate = async (s) => {
    if (!window.confirm("Reset this step to the default? Any unsaved + saved changes will be lost.")) return;
    setBusy({ key: s.template_key, action: "reset" });
    try {
      await axios.post(`${API}/email-drip/templates/${s.template_key}/reset`, {}, { headers: auth() });
      toast.success("Reset to default");
      setDrafts((p) => { const c = { ...p }; delete c[s.template_key]; return c; });
      setPreviewHtml((p) => { const c = { ...p }; delete c[s.template_key]; return c; });
      await reloadSteps();
    } catch (err) {
      toast.error("Reset failed");
    } finally {
      setBusy({ key: null, action: null });
    }
  };

  const testSend = async (s) => {
    setBusy({ key: s.template_key, action: "test" });
    try {
      // If there's an unsaved draft, save it first so the test reflects what they're editing.
      if (drafts[s.template_key]) {
        await saveTemplate(s);
      }
      const res = await axios.post(`${API}/email-drip/templates/${s.template_key}/test-send`, {}, { headers: auth() });
      toast.success(`Test sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally {
      setBusy({ key: null, action: null });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings/email-drip-reporting")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-reporting"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Drip Reporting</span>
        </button>

        <div className="mb-4">
          <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
            Email Drip Templates
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Edit the 5-step lead nurture sequence copy. Preview before saving, fire a test to your own inbox, or reset to default.
          </p>
        </div>

        {/* Merge-tag hint */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 mb-4">
          <strong className="block mb-1">Merge tags:</strong>
          • <span className="font-mono">{"{first_name}"}</span> in the <strong>subject</strong> (single braces)<br />
          • <span className="font-mono">{"{{first_name}}"}</span> in the <strong>body</strong> (double braces — Jinja2)<br />
          Available variables: <span className="font-mono">first_name</span>, <span className="font-mono">name</span>, <span className="font-mono">booking_link</span>, <span className="font-mono">estimate_total</span>, <span className="font-mono">service_city</span>, <span className="font-mono">unsubscribe_url</span>
        </div>

        <div className="space-y-3">
          {steps.map((s) => {
            const isOpen = expanded === s.template_key;
            const isDirty = !!drafts[s.template_key];
            const d = draftFor(s);
            const pv = previewHtml[s.template_key];
            const isBusy = (action) => busy.key === s.template_key && busy.action === action;
            return (
              <div key={s.template_key} className="bg-white rounded-xl border-2 border-zinc-100" data-testid={`step-card-${s.step}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : s.template_key)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {s.step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{d.subject}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      {formatDelay(s.offset_hours)}
                      {s.has_override && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                          <Check className="w-3 h-3" /> Edited
                        </span>
                      )}
                      {isDirty && (
                        <span className="ml-2 inline-flex items-center gap-1 text-orange-600">
                          <AlertTriangle className="w-3 h-3" /> Unsaved
                        </span>
                      )}
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                </button>

                {isOpen && (
                  <div className="border-t px-5 py-4 space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-zinc-600 mb-1 block">Subject</label>
                      <input
                        type="text"
                        value={d.subject}
                        onChange={(e) => setDraft(s.template_key, { subject: e.target.value })}
                        data-testid={`subject-${s.template_key}`}
                        className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-rose-300"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-zinc-600 mb-1 block flex items-center gap-2">
                        Body (HTML)
                        <span className="text-[10px] font-normal text-zinc-400">
                          {d.body_html?.length || 0} chars
                        </span>
                      </label>
                      <textarea
                        value={d.body_html}
                        onChange={(e) => setDraft(s.template_key, { body_html: e.target.value })}
                        rows={12}
                        spellCheck={false}
                        data-testid={`body-${s.template_key}`}
                        className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-rose-300 resize-y"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => renderPreview(s)}
                        disabled={isBusy("preview")}
                        data-testid={`preview-btn-${s.template_key}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-lg text-xs font-semibold hover:bg-zinc-200 disabled:opacity-50"
                      >
                        {isBusy("preview") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                        Preview
                      </button>
                      <button
                        onClick={() => testSend(s)}
                        disabled={busy.key === s.template_key}
                        data-testid={`test-btn-${s.template_key}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 text-zinc-700 rounded-lg text-xs font-semibold hover:bg-zinc-50 disabled:opacity-50"
                      >
                        {isBusy("test") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Send Test to Me
                      </button>
                      {s.has_override && (
                        <button
                          onClick={() => resetTemplate(s)}
                          disabled={busy.key === s.template_key}
                          data-testid={`reset-btn-${s.template_key}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 text-zinc-500 rounded-lg text-xs font-medium hover:bg-zinc-50 disabled:opacity-50"
                        >
                          {isBusy("reset") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          Reset to Default
                        </button>
                      )}
                      <button
                        onClick={() => saveTemplate(s)}
                        disabled={!isDirty || busy.key === s.template_key}
                        data-testid={`save-btn-${s.template_key}`}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-[#207024] text-white rounded-lg text-xs font-semibold hover:bg-[#185a1c] disabled:opacity-40"
                      >
                        {isBusy("save") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                      </button>
                    </div>

                    {pv && (
                      <div className="border-2 border-zinc-100 rounded-lg overflow-hidden bg-white">
                        <div className="bg-zinc-50 px-3 py-2 border-b border-zinc-100">
                          <div className="text-[10px] font-semibold text-zinc-400 uppercase">Preview</div>
                          <div className="text-sm font-semibold text-zinc-900 truncate">{pv.subject}</div>
                        </div>
                        <iframe
                          title="Email preview"
                          srcDoc={pv.html}
                          sandbox=""
                          className="w-full h-[420px] bg-white"
                          data-testid={`preview-iframe-${s.template_key}`}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
